/* cardframes — Anordnungslogik für den Shop-Konfigurator.
 *
 * Portierung von konfigurator/layout.py. Rechnet bewusst mit den aufgerundeten
 * Maßen aus dem Katalog und ohne Laserabzug: Was hier aufgeht, geht in der
 * Fertigung erst recht auf. Die verbindliche Anordnung entsteht später im
 * Werkzeug, nicht hier.
 */
(function () {
  'use strict';

  function chunk(items, columns) {
    const rows = [];
    for (let i = 0; i < items.length; i += columns) rows.push(items.slice(i, i + columns));
    return rows;
  }

  const rowWidth = (row, gap) => row.reduce((s, i) => s + i.w, 0) + gap * (row.length - 1);
  const rowHeight = (row) => Math.max(...row.map((i) => i.h));
  const stackHeight = (rows, gap) =>
    rows.reduce((s, r) => s + rowHeight(r), 0) + gap * (rows.length - 1);

  function fits(rows, usableW, usableH, gap) {
    if (rows.some((r) => rowWidth(r, gap) > usableW + 1e-9)) return false;
    return stackHeight(rows, gap) <= usableH + 1e-9;
  }

  /* Wie weit liegen waagerechte und senkrechte Luft auseinander? Klein = ruhiges Bild. */
  function balance(rows, usableW, usableH) {
    const gapY = (usableH - rows.reduce((s, r) => s + rowHeight(r), 0)) / (rows.length + 1);
    const gapX = Math.min(
      ...rows.map((r) => (usableW - r.reduce((s, i) => s + i.w, 0)) / (r.length + 1))
    );
    return Math.abs(gapX - gapY);
  }

  /* Bewertungen der Reihe nach vergleichen, wie ein Tupel in Python. */
  function besser(a, b) {
    for (let i = 0; i < Math.max(a.length, b.length); i++) {
      if (a[i] !== b[i]) return a[i] < b[i];
    }
    return false;
  }

  const emptySlots = (count, columns) =>
    count && columns ? (columns - (count % columns)) % columns : 0;

  /* Jede Tasche bekommt eine Kennung (psa#2) und eine Beschriftung (2 PSA).
   * Gezählt wird pro Typ über alle Zeilen hinweg, damit eine Drehung immer
   * genau eine Tasche trifft. */
  function expand(requests, rotated, overrides) {
    const seen = {};
    const items = [];
    for (const { holder, count } of requests) {
      for (let n = 0; n < count; n++) {
        seen[holder.id] = (seen[holder.id] || 0) + 1;
        const number = seen[holder.id];
        const key = holder.id + '#' + number;
        const forced = Object.prototype.hasOwnProperty.call(overrides, key);
        const turn = forced ? overrides[key] : rotated;
        items.push({
          w: turn ? holder.h : holder.w,
          h: turn ? holder.w : holder.h,
          label: number + ' ' + holder.name,
          id: holder.id,
          key,
          rotated: turn,
          forced,
        });
      }
    }
    return items;
  }

  /* Bestes Raster für eine einheitliche Ausrichtung, oder null. */
  function grid(items, usableW, usableH, gap) {
    let best = null;
    for (let cols = 1; cols <= items.length; cols++) {
      const rows = chunk(items, cols);
      if (!fits(rows, usableW, usableH, gap)) continue;
      const key = [emptySlots(items.length, cols), balance(rows, usableW, usableH)];
      if (!best || besser(key, best.key)) best = { key, rows };
    }
    return best;
  }

  /* Oben ein Block aufrecht, darunter einer quer.
   *
   * Geteilt wird nach Ausrichtung, nicht nach Position: von Hand festgelegte
   * Karten wandern immer in den Block ihrer Ausrichtung, egal wo sie in der
   * Bestückung stehen. Sonst zöge eine gedrehte Karte in der Mitte alle
   * dahinterliegenden mit in den Querblock. */
  function mixed(requests, usableW, usableH, gap, overrides) {
    const upright = expand(requests, false, overrides);
    const crossways = expand(requests, true, overrides);
    const count = upright.length;
    if (count > 60) return null;

    const free = [];
    upright.forEach((item, i) => {
      if (!item.forced) free.push(i);
    });

    /* k freie Karten aufrecht, der Rest quer. Reihenfolge bleibt erhalten. */
    function splitAt(k) {
      const querFree = new Set(free.slice(k));
      const blockA = [];
      const blockB = [];
      for (let i = 0; i < count; i++) {
        const item = upright[i].forced || !querFree.has(i) ? upright[i] : crossways[i];
        (item.rotated ? blockB : blockA).push(item);
      }
      return [blockA, blockB];
    }

    function blocks(items) {
      if (!items.length) return [{ rows: [], height: 0, empty: 0 }];
      const out = [];
      for (let cols = 1; cols <= items.length; cols++) {
        const rows = chunk(items, cols);
        if (rows.some((r) => rowWidth(r, gap) > usableW + 1e-9)) continue;
        out.push({ rows, height: stackHeight(rows, gap), empty: emptySlots(items.length, cols) });
      }
      return out;
    }

    let best = null;
    for (let split = 0; split <= free.length; split++) {
      const [blockA, blockB] = splitAt(split);
      for (const a of blocks(blockA)) {
        for (const b of blocks(blockB)) {
          const rows = a.rows.concat(b.rows);
          if (!rows.length) continue;
          const height = a.height + b.height + (a.rows.length && b.rows.length ? gap : 0);
          if (height > usableH + 1e-9) continue;
          // So wenige ungefragte Drehungen wie möglich, dann volle Reihen,
          // dann das ruhigere Bild.
          const key = [free.length - split, a.empty + b.empty, balance(rows, usableW, usableH)];
          if (!best || besser(key, best.key)) best = { key, rows };
        }
      }
    }
    return best;
  }

  function pack(requests, frameW, frameH, margin, gap, overrides) {
    const usableW = frameW - 2 * margin;
    const usableH = frameH - 2 * margin;
    if (usableW <= 0 || usableH <= 0) return null;

    const hasForced = Object.keys(overrides).length > 0;

    // Aufrecht schlägt quer, quer schlägt gemischt. Sind Taschen von Hand
    // festgelegt, kommt Gemischt vor "alle quer" — sonst zöge das einheitliche
    // Drehen die freien Taschen mit.
    for (const rotated of [false, true]) {
      if (rotated && hasForced) {
        const m = mixed(requests, usableW, usableH, gap, overrides);
        if (m) return m.rows;
      }
      const g = grid(expand(requests, rotated, overrides), usableW, usableH, gap);
      if (g) return g.rows;
    }

    const m = mixed(requests, usableW, usableH, gap, overrides);
    return m ? m.rows : null;
  }

  /* Anordnung berechnen. Koordinaten mit Ursprung oben links (Zeichenrichtung
   * des Vorschaubilds), Einheit = dieselbe wie im Katalog. */
  function plan(opts) {
    const frameW = opts.frame.w;
    const frameH = opts.frame.h;
    const margin = opts.margin;
    const gap = opts.gap;
    const overrides = opts.overrides || {};
    const requests = opts.requests.filter((r) => r.count > 0);
    const total = requests.reduce((s, r) => s + r.count, 0);

    if (!total) return { ok: false, error: 'leer' };

    const usableW = frameW - 2 * margin;
    const usableH = frameH - 2 * margin;
    const rows = pack(requests, frameW, frameH, margin, gap, overrides);
    if (!rows) {
      const ohne = Object.keys(overrides).length
        ? pack(requests, frameW, frameH, margin, gap, {})
        : null;
      return { ok: false, error: 'passt nicht', ohneDrehungen: Boolean(ohne) };
    }

    const heights = rows.map(rowHeight);
    const freeV = usableH - heights.reduce((s, h) => s + h, 0);

    // Symmetrisch verteilen: die freie Fläche gleichmäßig auf Rand und Stege.
    const gapY = freeV / (rows.length + 1);
    let y = margin + gapY;

    const pockets = [];
    rows.forEach((row, ri) => {
      const rowH = heights[ri];
      const freeH = usableW - row.reduce((s, i) => s + i.w, 0);
      const gapX = freeH / (row.length + 1);
      let x = margin + gapX;

      for (const item of row) {
        pockets.push({
          x,
          y: y + (rowH - item.h) / 2,
          w: item.w,
          h: item.h,
          label: item.label,
          id: item.id,
          key: item.key,
          rotated: item.rotated,
          forced: item.forced,
        });
        x += item.w + gapX;
      }
      y += rowH + gapY;
    });

    const flags = new Set(pockets.map((p) => p.rotated));
    return {
      ok: true,
      pockets,
      rows: rows.length,
      columns: Math.max(...rows.map((r) => r.length)),
      orientation: flags.size > 1 ? 'gemischt' : flags.has(true) ? 'quer' : 'aufrecht',
    };
  }

  /* Wie viele Halter eines Typs passen höchstens in einen Rahmen? Für die
   * Mengenbegrenzung in der Oberfläche. */
  function capacity(frame, holder, margin, gap, limit) {
    let best = 0;
    for (let n = 1; n <= (limit || 40); n++) {
      if (!plan({ frame, requests: [{ holder, count: n }], margin, gap }).ok) break;
      best = n;
    }
    return best;
  }

  /* In welche anderen Rahmen würde dieselbe Bestückung passen? Kleinster
   * zuerst, damit der Kunde den nächstgrößeren angeboten bekommt und nicht
   * gleich den teuersten. */
  function passendeRahmen(frames, requests, margin, gap, overrides) {
    return frames
      .slice()
      .sort((a, b) => a.w * a.h - b.w * b.h)
      .filter((f) => plan({ frame: f, requests, margin, gap, overrides }).ok);
  }

  window.CardframesLayout = { plan, capacity, passendeRahmen };
})();
